import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight,
  Bot,
  Building2,
  CreditCard,
  DollarSign,
  RefreshCw,
  Info,
  Home,
  Wrench,
  FileText,
  Settings,
  WifiOff,
  Wifi,
  Webhook,
} from "lucide-react";
import { RouterOutputs } from "../../trpc";
import {
  AGENT_TOPIC_COLLECTIONS,
  AGENT_TOPIC_CUSTOM,
  AGENT_TOPIC_INQUIRIES,
  AGENT_TOPIC_MAINTENANCE,
  AGENT_TOPIC_SALES,
  AGENT_TOPIC_SOURCEPROPERTIES,
  PIPELINE_ID_COLLECTIONS,
  PIPELINE_ID_DEFAULT,
  PIPELINE_ID_GENERAL,
  PIPELINE_ID_MAINTENANCE,
  PIPELINE_ID_SOURCEPROPERTIES,
  STANDARD_PIPELINE_IDS,
} from "../../../../agent/src/common/types";
import { mapAgentTopic } from "../../utils/valueMap";

type Agent = RouterOutputs["agents"]["list"][0];

type Props = {
  agent: Agent;
  pipelineDisplayNameById?: Record<string, string>;
  projectName?: string;
  showProjectButton?: boolean;
  isSelected: boolean;
  onClick: () => void;
  onConnectClick?: () => void;
  onProjectClick?: () => void;
  onMigrateClick?: () => void;
  showMigrateButton?: boolean;
  isMigrating?: boolean;
  onSetPrimary?: () => void;
  showPrimarySelector?: boolean;
  primaryGroupName?: string;
  isSettingPrimary?: boolean;
};

const getAgentDisplayName = (
  agent: { pipelineId?: string; topic?: string; agentType?: string },
  pipelineDisplayNameById?: Record<string, string>
): string => {
  if (agent.agentType === "external") return "Externo";
  const pipelineId = agent.pipelineId;
  if (pipelineId && pipelineDisplayNameById?.[pipelineId])
    return pipelineDisplayNameById[pipelineId];
  if (pipelineId === undefined && agent.topic) return mapAgentTopic[agent.topic] ?? "Personalizado";
  return "Personalizado";
};

const getAgentIcon = (agent: { pipelineId?: string; topic?: string; agentType?: string }) => {
  if (agent.agentType === "external") return Webhook;
  const pipelineId = agent.pipelineId;
  if (pipelineId === PIPELINE_ID_SOURCEPROPERTIES) return Home;
  if (pipelineId === PIPELINE_ID_MAINTENANCE) return Wrench;
  if (pipelineId === PIPELINE_ID_GENERAL) return FileText;
  if (pipelineId === PIPELINE_ID_COLLECTIONS) return CreditCard;
  if (pipelineId) return Settings;
  const topic = agent.topic;
  if (topic === AGENT_TOPIC_SALES) return DollarSign;
  if (topic === AGENT_TOPIC_SOURCEPROPERTIES) return Home;
  if (topic === AGENT_TOPIC_MAINTENANCE) return Wrench;
  if (topic === AGENT_TOPIC_INQUIRIES) return FileText;
  if (topic === AGENT_TOPIC_COLLECTIONS) return CreditCard;
  if (topic === AGENT_TOPIC_CUSTOM) return Settings;
  return Bot;
};

const getAgentDescription = (agent: {
  pipelineId?: string;
  topic?: string;
  agentType?: string;
}): string => {
  if (agent.agentType === "external") return "Agente externo que reenvía mensajes vía webhook";
  const pipelineId = agent.pipelineId;
  if (pipelineId === PIPELINE_ID_DEFAULT) return "Especializado en ventas y arriendos";
  if (pipelineId === PIPELINE_ID_SOURCEPROPERTIES)
    return "Especializado en captación de inmuebles para ventas y arriendo";
  if (pipelineId === PIPELINE_ID_MAINTENANCE)
    return "Encargado de recibir y registrar solicitudes de mantenimientos";
  if (pipelineId === PIPELINE_ID_GENERAL)
    return "Agente de propósito general para responder preguntas administrativas o redirigir a un agente humano";
  if (pipelineId === PIPELINE_ID_COLLECTIONS)
    return "Especializado en gestión de cartera y cobranza";
  if (pipelineId) return "Agente personalizado";
  const topic = agent.topic;
  if (topic === AGENT_TOPIC_SOURCEPROPERTIES)
    return "Especializado en captación de inmuebles para ventas y arriendo";
  if (topic === AGENT_TOPIC_SALES) return "Especializado en ventas y arriendos";
  if (topic === AGENT_TOPIC_MAINTENANCE)
    return "Encargado de recibir y registrar solicitudes de mantenimientos";
  if (topic === AGENT_TOPIC_INQUIRIES)
    return "Agente de propósito general para responder preguntas administrativas o redirigir a un agente humano";
  if (topic === AGENT_TOPIC_COLLECTIONS) return "Especializado en gestión de cartera y cobranza";
  if (topic === AGENT_TOPIC_CUSTOM) return "Agente personalizado";
  return "Agente especializado";
};

const AgentCard = ({
  agent,
  pipelineDisplayNameById,
  projectName,
  showProjectButton = false,
  isSelected,
  onClick,
  onConnectClick,
  onProjectClick,
  onMigrateClick,
  showMigrateButton = false,
  isMigrating = false,
  onSetPrimary,
  showPrimarySelector = false,
  isSettingPrimary = false,
}: Props): JSX.Element => {
  const displayName = getAgentDisplayName(agent, pipelineDisplayNameById);
  const AgentIcon = getAgentIcon(agent);
  const description = getAgentDescription(agent);
  const isOnline = !!agent.whatsappConnectionId;

  const handleConnectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onConnectClick?.();
  };

  const handleProjectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onProjectClick?.();
  };

  const handleMigrateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMigrateClick?.();
  };

  const handlePrimaryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSetPrimary?.();
  };

  return (
    <div
      className={cn(
        "relative flex h-full w-full cursor-pointer flex-col rounded-lg border-2 p-6 transition-all duration-200 hover:-translate-y-px hover:shadow-[0px_2px_12px_rgba(0,0,0,0.08)]",
        isSelected
          ? "border-[#5C28F5] bg-[#F8F6FF]"
          : "border-gray-200 bg-white hover:border-gray-300"
      )}
      onClick={onClick}
    >
      {/* Selection checkmark */}
      {isSelected && (
        <div className="absolute right-4 top-4 flex size-6 items-center justify-center rounded-full bg-[#5C28F5]">
          <span className="text-sm text-white">✓</span>
        </div>
      )}

      {/* Agent icon and name */}
      <div className="mb-3 flex items-start gap-3 rounded bg-gray-50 p-2">
        <div className="flex items-center justify-center rounded-md bg-[#5C28F5] p-2">
          <AgentIcon className="size-5 text-white" />
        </div>
        <div className="flex flex-col">
          <p className="text-lg font-bold text-gray-800">{displayName}</p>
          {agent.pipelineId != null && agent.agentName?.trim() ? (
            <p className="text-sm text-gray-600">{agent.agentName}</p>
          ) : null}
        </div>
      </div>

      {/* Status and Popular badges */}
      <div className="mb-3 flex items-center gap-2">
        <span className="rounded-full bg-[#E3FFE4] px-2.5 py-0.5 text-xs font-medium text-[#2D8A3F]">
          Popular
        </span>
        <span
          className={cn(
            "flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
            isOnline ? "bg-[#E3FFE4] text-[#2D8A3F]" : "bg-red-50 text-red-700"
          )}
        >
          {isOnline ? <Wifi className="size-3" /> : <WifiOff className="size-3" />}
          {isOnline ? "Online" : "Offline"}
        </span>
      </div>

      {/* WhatsApp phone number */}
      {agent.whatsappPhoneNumber && (
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">WhatsApp:</span>
            <span className="text-sm font-semibold text-gray-700">{agent.whatsappPhoneNumber}</span>
          </div>
        </div>
      )}

      {/* Linked project (development merchants only — projectName is undefined otherwise) */}
      {agent.projectId && projectName && (
        <div className="mb-3 flex items-center gap-2">
          <Building2 className="size-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-600">Proyecto:</span>
          <span className="text-sm font-semibold text-gray-700">{projectName}</span>
        </div>
      )}

      {/* Primary Agent Selector */}
      {showPrimarySelector && agent.whatsappConnectionId && (
        <div className="mb-3">
          {agent.isPrimary ? (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-[#5C28F5] px-3 py-1 text-xs font-semibold text-white">
                Agente Principal
              </span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex cursor-help items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Info className="size-3 text-gray-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    El agente principal será el que tomará la conversación con usuarios nuevos
                    cuando escriban a la línea
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={handlePrimaryClick}
              disabled={isSettingPrimary}
            >
              {isSettingPrimary ? "Estableciendo..." : "Establecer como Principal"}
            </Button>
          )}
        </div>
      )}

      {/* Connect/Change WhatsApp button */}
      <Button size="sm" variant="outline" className="mb-3 w-full" onClick={handleConnectClick}>
        {isOnline ? <RefreshCw className="mr-2 size-4" /> : <Wifi className="mr-2 size-4" />}
        {isOnline ? "Cambiar WhatsApp" : "Conectar WhatsApp"}
      </Button>

      {/* Link/Change Project button (development merchants only) */}
      {showProjectButton && (
        <Button
          size="sm"
          variant="outline"
          className="mb-3 w-full"
          onClick={handleProjectClick}
        >
          <Building2 className="mr-2 size-4" />
          {agent.projectId ? "Cambiar Proyecto" : "Vincular Proyecto"}
        </Button>
      )}

      {/* Migrate to V2 button (development merchants whose agent has no assistantId) */}
      {showMigrateButton && (
        <Button
          size="sm"
          variant="outline"
          className="mb-3 w-full border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={handleMigrateClick}
          disabled={isMigrating}
        >
          <ArrowUpRight className="mr-2 size-4" />
          {isMigrating ? "Migrando…" : "Migrar a V2"}
        </Button>
      )}

      {/* Agent description */}
      <p className="mb-3 grow text-sm leading-relaxed text-gray-600">{description}</p>

      {/* Agent capabilities */}
      <div className="flex flex-wrap gap-2">
        {(agent.pipelineId === PIPELINE_ID_SOURCEPROPERTIES ||
          agent.topic === AGENT_TOPIC_SOURCEPROPERTIES) && (
          <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
            Ventas y Arriendos
          </span>
        )}
        {(agent.pipelineId === PIPELINE_ID_DEFAULT || agent.topic === AGENT_TOPIC_SALES) && (
          <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
            Ventas y arriendos
          </span>
        )}
        {(agent.pipelineId === PIPELINE_ID_MAINTENANCE ||
          agent.topic === AGENT_TOPIC_MAINTENANCE) && (
          <span className="rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
            Mantenimientos
          </span>
        )}
        {(agent.pipelineId === PIPELINE_ID_GENERAL || agent.topic === AGENT_TOPIC_INQUIRIES) && (
          <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
            General
          </span>
        )}
        {(agent.pipelineId === PIPELINE_ID_COLLECTIONS ||
          agent.topic === AGENT_TOPIC_COLLECTIONS) && (
          <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-800">
            Cartera
          </span>
        )}
        {(agent.pipelineId &&
          !STANDARD_PIPELINE_IDS.includes(
            agent.pipelineId as (typeof STANDARD_PIPELINE_IDS)[number]
          )) ||
        agent.topic === AGENT_TOPIC_CUSTOM ? (
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
            Personalizado
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default AgentCard;
